'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Field, Form, Icon } from '@plura/client/components';
import { cn } from '@plura/shared/utils';
import { Button } from '../../ui/Button';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod/v3';

const contactFormSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  email: z.string().email({ message: 'Please provide a valid email' }).min(1, { message: 'Email is required' }),
  company: z.string(),
  message: z.string().min(1, { message: 'Message is required' }),
});

export type ContactFormValues = z.infer<typeof contactFormSchema>;

const ContactSection = () => {
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [messageSent, setMessageSent] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const form = useForm<ContactFormValues>({
    // zodResolver is typed for default `zod` (v4); Zod v4's ZodError uses `.issues` only, so the
    // resolver rethrows and breaks validation UI. Schema is built with `zod/v3` so `.errors` matches the resolver.
    resolver: zodResolver(contactFormSchema as unknown as Parameters<typeof zodResolver>[0]),
    defaultValues: {
      name: '',
      email: '',
      company: '',
      message: '',
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const submit = async (data: ContactFormValues) => {
    try {
      setIsSending(true);
      setSubmissionError(null);

      const result = await fetch('/api/sendEmail', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      const resultJson: boolean = await result.json();

      if (!resultJson) {
        throw new Error('Failed to send email');
      }

      setMessageSent(true);
    } catch {
      const message = 'There seemed to be an issue. Please try again.';
      setSubmissionError(message);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="w-full py-12 pb-[100px] pt-[50px]">
      <div className="landing-container flex flex-row items-start justify-stretch gap-[120px] max-lg:flex-col max-lg:gap-[60px]">
        <div className="flex w-1/2 flex-col items-stretch justify-stretch gap-5 max-lg:w-full">
          {!messageSent ? (
            <Form form={form} onSubmit={handleSubmit(submit)} className="flex w-full flex-col gap-5">
              <div className="flex w-full flex-row items-stretch justify-stretch gap-[30px] max-md:flex-col">
                <Field
                  id="name"
                  className="w-1/2 max-md:w-full"
                  type="text"
                  placeholder="Full name"
                  label="Name"
                  {...register('name')}
                  error={errors.name?.message}
                />
                <Field
                  id="company"
                  className="w-1/2 max-md:w-full"
                  type="text"
                  placeholder="Company"
                  label="Company"
                  {...register('company')}
                  error={errors.company?.message}
                />
              </div>
              <Field
                id="email"
                className="w-full"
                type="email"
                placeholder="Email"
                label="Email"
                {...register('email')}
                error={errors.email?.message}
              />
              <Field
                as="textarea"
                id="message"
                className="w-full"
                placeholder="Message"
                label="Message"
                {...register('message')}
                error={errors.message?.message}
              />
              <div className="mt-0">
                <Button className="w-full disabled:pointer-events-none disabled:opacity-60" disabled={isSending} type="submit">
                  {isSending ? 'Sending…' : 'Send message'}
                </Button>
              </div>
            </Form>
          ) : (
            <div className="flex min-h-[310px] w-full flex-col items-center justify-center gap-2.5 rounded-xl px-5 py-[60px]">
              <h4 className="font-heading text-[28px] font-medium tracking-[-0.02em] text-dark">Thank you for your message!</h4>
              <p className="font-body mb-5 text-base leading-normal text-[#555555]">We will get back to you as soon as possible.</p>
              <Button href="/">Visit the homepage</Button>
            </div>
          )}
          {submissionError && (
            <div className={cn('w-full font-body text-sm text-red')}>{submissionError}</div>
          )}
        </div>
        <div className="flex max-w-[300px] flex-1 flex-col items-start justify-stretch gap-[60px] max-lg:max-w-none">
          <div className="w-full">
            <h4 className="font-heading mb-1.5 text-[22px] font-medium tracking-[-0.01em] text-dark">Contact us</h4>
            <p className="font-body mb-5 text-[15px] leading-normal text-[#555555]">
              Send us a message and we&apos;ll get back to you as soon as possible.
            </p>
            <div>
              <a
                href="mailto:support@formsuite.co"
                className="font-body inline-flex items-center justify-start gap-1.5 text-base text-dark transition-colors hover:text-green"
              >
                <Icon name="send" className="!size-4 shrink-0 fill-current" aria-hidden />
                <span className="underline">support@formsuite.co</span>
              </a>
            </div>
          </div>
          <div className="w-full">
            <h4 className="font-heading mb-1.5 text-[22px] font-medium tracking-[-0.01em] text-dark">See our socials</h4>
            <p className="font-body mb-5 text-[15px] leading-normal text-[#555555]">
              Follow us on our socials to stay up to date with the latest news and updates.
            </p>
            <div className="flex flex-row items-center justify-start gap-5">
              <a
                href="https://www.facebook.com/people/Formsuite/61571974303966"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Formsuite on Facebook"
                className="text-xl text-dark transition-colors hover:text-green"
              >
                <Icon name="facebook" className="!size-5 fill-current" />
              </a>
              <a
                href="https://x.com/formsuiteco"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Formsuite on X"
                className="text-xl text-dark transition-colors hover:text-green"
              >
                <Icon name="twitter" className="!size-5 fill-current" />
              </a>
              <a
                href="https://www.linkedin.com/company/formsuite"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Formsuite on LinkedIn"
                className="text-xl text-dark transition-colors hover:text-green"
              >
                <Icon name="linkedin" className="!size-5 fill-current" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactSection;

export { contactFormSchema };
